import { useSession } from "@src/appV2/Auth/api/useSession";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { showErrorToast, showInfoToast } from "@src/appV2/lib/Notifications";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  EXPIRY_CHECK_INTERVAL_MS,
  EXPIRY_MS,
  IMPERSONATION_APP_EVENTS,
  IMPERSONATION_USER_EVENTS,
  MAX_SESSIONS,
} from "../constants";
import type { ImpersonationSession, ImpersonationSessionType } from "../types";
import { buildImpersonationUrl } from "../utils/buildImpersonationUrl";
import {
  generateSessionId,
  getDefaultWindowSize,
  getInitialBalloonPosition,
  getInitialWindowPosition,
  loadPersistedSessions,
  nextZIndex,
  persistSessions,
} from "../utils/sessionHelpers";
import { useViewportClamp } from "./useViewportClamp";

export function useImpersonationSessionsHook() {
  const { adminUserId: employeeId } = useSession();
  const [sessions, setSessions] = useState<ImpersonationSession[]>(loadPersistedSessions);
  const sessionsRef = useRef(sessions);
  const isInitialMount = useRef(true);

  useLayoutEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    persistSessions(sessions);
  }, [sessions]);

  const openSession = useCallback(
    (
      sessionType: ImpersonationSessionType,
      baseAppUrl: string,
      targetId: string,
      targetName: string,
      cognitoUsername: string
    ) => {
      let impersonateUrl: string;
      try {
        impersonateUrl = buildImpersonationUrl(baseAppUrl, targetId, cognitoUsername);
      } catch {
        showErrorToast("Unable to open impersonation session. Please try again.");
        return;
      }
      let event: { name: string; sessionId: string } | "limit" | undefined;

      setSessions((previous) => {
        const existing = previous.find(
          (session) => session.sessionType === sessionType && session.targetId === targetId
        );
        if (existing) {
          const zIndex = nextZIndex();
          event = { name: "restored", sessionId: existing.id };
          return previous.map((session) =>
            session.id === existing.id
              ? { ...session, isMinimized: false, isActive: true, zIndex }
              : session
          );
        }

        if (previous.length >= MAX_SESSIONS) {
          event = "limit";
          return previous;
        }
        const size = getDefaultWindowSize(sessionType);
        const newSession: ImpersonationSession = {
          id: generateSessionId(),
          sessionType,
          targetId,
          targetName,
          cognitoUsername,
          url: impersonateUrl,
          startedAt: Date.now(),
          isMinimized: false,
          isActive: true,
          position: getInitialWindowPosition(previous.length, size),
          size,
          balloonPosition: getInitialBalloonPosition(previous.length),
          zIndex: nextZIndex(),
        };

        event = { name: "opened", sessionId: newSession.id };
        return [...previous, newSession];
      });

      if (event === "limit") {
        logEvent(IMPERSONATION_APP_EVENTS.SESSION_LIMIT_REACHED, {
          employeeId,
          targetId,
          targetName,
          sessionType,
        });
        showErrorToast(`Maximum ${MAX_SESSIONS} impersonation sessions. Close one first.`);
      } else if (event?.name === "restored") {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_RESTORED, {
          employeeId,
          targetId,
          targetName,
          sessionType,
          sessionId: event.sessionId,
        });
      } else if (event?.name === "opened") {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_STARTED, {
          employeeId,
          targetId,
          targetName,
          sessionType,
          sessionId: event.sessionId,
        });
      }
    },
    [employeeId]
  );

  const closeSession = useCallback(
    (id: string) => {
      const session = sessionsRef.current.find((s) => s.id === id);
      if (session) {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_CLOSED, {
          employeeId,
          targetId: session.targetId,
          targetName: session.targetName,
          sessionType: session.sessionType,
          sessionId: id,
        });
      }

      setSessions((previous) => previous.filter((s) => s.id !== id));
    },
    [employeeId]
  );

  const minimizeSession = useCallback(
    (id: string) => {
      const session = sessionsRef.current.find((s) => s.id === id);
      if (session) {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_MINIMIZED, {
          employeeId,
          targetId: session.targetId,
          targetName: session.targetName,
          sessionType: session.sessionType,
          sessionId: id,
        });
      }

      setSessions((previous) =>
        previous.map((s) => (s.id === id ? { ...s, isMinimized: true } : s))
      );
    },
    [employeeId]
  );

  const restoreSession = useCallback(
    (id: string) => {
      const session = sessionsRef.current.find((s) => s.id === id);
      if (session) {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_RESTORED, {
          employeeId,
          targetId: session.targetId,
          targetName: session.targetName,
          sessionType: session.sessionType,
          sessionId: id,
        });
      }

      setSessions((previous) =>
        previous.map((s) => (s.id === id ? { ...s, isMinimized: false, isActive: true } : s))
      );
    },
    [employeeId]
  );

  const updateBalloonPosition = useCallback((id: string, position: { x: number; y: number }) => {
    setSessions((previous) =>
      previous.map((session) =>
        session.id === id ? { ...session, balloonPosition: position } : session
      )
    );
  }, []);

  const updateWindowPosition = useCallback((id: string, position: { x: number; y: number }) => {
    setSessions((previous) =>
      previous.map((session) => (session.id === id ? { ...session, position } : session))
    );
  }, []);

  const updateWindowSize = useCallback((id: string, size: { width: number; height: number }) => {
    setSessions((previous) =>
      previous.map((session) => (session.id === id ? { ...session, size } : session))
    );
  }, []);

  const bringToFront = useCallback((id: string) => {
    const zIndex = nextZIndex();
    setSessions((previous) =>
      previous.map((session) => (session.id === id ? { ...session, zIndex } : session))
    );
  }, []);

  const updateSessionExpiresAt = useCallback((id: string, expiresAt: number) => {
    setSessions((previous) =>
      previous.map((session) => (session.id === id ? { ...session, expiresAt } : session))
    );
  }, []);

  // Deactivate expired sessions (close iframe but keep pill)
  useEffect(() => {
    if (sessions.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const expiredSessions: Array<{
        employeeId: string | undefined;
        targetId: string;
        targetName: string;
        sessionType: ImpersonationSessionType;
        sessionId: string;
      }> = [];

      setSessions((previous) => {
        const kept: ImpersonationSession[] = [];
        for (const session of previous) {
          const isExpired = session.expiresAt
            ? now >= session.expiresAt
            : now - session.startedAt >= EXPIRY_MS;
          if (isExpired && session.isActive) {
            expiredSessions.push({
              employeeId,
              targetId: session.targetId,
              targetName: session.targetName,
              sessionType: session.sessionType,
              sessionId: session.id,
            });
            // Workplace sessions cannot be refreshed — auto-close on expiry
            if (session.sessionType === "workplace") {
              showInfoToast(`Impersonation session for ${session.targetName} has expired.`);
              continue;
            }

            kept.push({ ...session, isActive: false, isMinimized: true });
          } else {
            kept.push(session);
          }
        }

        return kept;
      });

      for (const expired of expiredSessions) {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_EXPIRED, expired);
      }
    }, EXPIRY_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [employeeId, sessions.length]);

  useViewportClamp(sessions.length, setSessions);

  return {
    sessions,
    openSession,
    closeSession,
    minimizeSession,
    restoreSession,
    bringToFront,
    updateBalloonPosition,
    updateSessionExpiresAt,
    updateWindowPosition,
    updateWindowSize,
  };
}
